import { useEffect, useState } from "react";
import api, { formatRand, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { ViewHeader, EmptyState, StatusPill, formatDate } from "@/components/admin/AdminShared";

const STATUSES = ["Processing", "Packed", "Shipped", "Delivered", "Cancelled"];

export const OrdersView = ({ onBack, onChanged }) => {
  const [orders, setOrders] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
  }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success("Order status updated");
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.order_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success("Order deleted");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-orders-view">
      <ViewHeader onBack={onBack} title={`All Orders (${orders?.length ?? "…"})`} subtitle="Update delivery status or delete orders." />
      {orders && orders.length === 0 ? (
        <EmptyState>No orders yet.</EmptyState>
      ) : (
        <div className="space-y-4" data-testid="admin-orders-list">
          {(orders || []).map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.order_number}`} className="bg-white rounded-2xl border border-purple-100 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-bold text-purple-950">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{o.user_email} · {formatDate(o.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill tone={o.payment_status === "Paid" ? "green" : "amber"}>{o.payment_status}</StatusPill>
                  <span className="font-bold brand-gradient-text">{formatRand(o.total)}</span>
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger data-testid={`admin-order-status-${o.id}`} className="w-36 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button data-testid={`admin-order-delete-${o.id}`} onClick={() => remove(o)} className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-purple-50 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                <p><strong className="text-gray-800">Ship to:</strong> {o.shipping?.full_name}, {o.shipping?.address}, {o.shipping?.city} {o.shipping?.postal_code}</p>
                <p><strong className="text-gray-800">Items:</strong> {o.items.map((i) => `${i.name} (${[i.colour, i.size].filter(Boolean).join(", ")}) x${i.quantity}`).join("; ")}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
